import React from "react";
import { X, ArrowClockwise, CircleNotch } from "@phosphor-icons/react";
import Avatar2D from "@/components/Avatar2D";
import { racePlaces } from "@/components/RacePodium";

// The end-of-race pop-up (Jack, 2026-10-25, from his OK'd mockup): both
// raiders, the winner big with a gold 1st badge, a gold banner when you win
// and dark blue when you lose, how many rows each still had to go and the
// round it ended in, then one big "Race again" with View board and Lobby.
// `racers`: [{ name, character, rows, won, you }] as for RacePodium.
// Races against people pass `rematchState` ("idle" | "pending" | "incoming" |
// "declined", as GameOverModal) with onAccept/onDecline/onCancel, and `fixed`
// to cover the whole page.

function rowsText(racers) {
  return racers.filter((r) => r.rows != null).map((r) => `${r.you ? "You" : r.name} ${r.rows}`).join(" · ");
}

export default function RaceResult({
  racers, round, reason, onAgain, againLabel = "Race again", onView, onLobby,
  fixed = false, eloDelta = null, rematchState = "idle", oppName = "", onAccept, onDecline, onCancel,
}) {
  const places = racePlaces(racers || []);
  const winner = places[0];
  const youWon = !!winner?.you;
  const rest = places.slice(1);
  const rows = rowsText(places);
  return (
    <div className={`${fixed ? "fixed" : "absolute"} inset-0 z-50 flex items-center justify-center bg-[rgba(5,8,15,.62)] backdrop-blur-[2px] p-4`} data-testid="race-result">
      <div className="relative w-full max-w-[304px] rounded-[20px] overflow-hidden bg-card text-card-foreground border border-border shadow-2xl">
        <div
          className="relative h-[118px] flex items-end justify-center gap-[18px]"
          style={{ background: youWon ? "linear-gradient(135deg,#f59e0b,#b45309)" : "linear-gradient(135deg,#1e293b,#3730a3)" }}
        >
          <button type="button" aria-label="Close and view the board" title="View the board" onClick={onView}
            className="absolute top-2 right-2 p-1.5 rounded-full text-white/80 hover:text-white" data-testid="race-result-close">
            <X size={18} weight="bold" />
          </button>
          {rest.map((r, i) => (
            <div key={i} className="flex flex-col items-center translate-y-[34px]">
              <div className="w-[58px] h-[58px] rounded-full overflow-hidden border-[3px] border-card bg-muted opacity-85">
                <Avatar2D character={r.character} pose="idle" className="w-[130%] h-[130%] -ml-[15%] -mt-[2%]" />
              </div>
              <span className="relative z-[1] -mt-3 text-[11px] font-extrabold px-2 py-0.5 rounded-full border-2 border-card bg-slate-400 text-slate-950">
                {i === 0 ? "2nd" : "3rd"}
              </span>
            </div>
          ))}
          {winner && (
            <div className="flex flex-col items-center translate-y-[40px]">
              <div className="w-[84px] h-[84px] rounded-full overflow-hidden border-[3px] border-amber-400 bg-muted">
                <Avatar2D character={winner.character} pose="cheer" className="w-[130%] h-[130%] -ml-[15%] -mt-[2%]" />
              </div>
              <span className="relative z-[1] -mt-3 text-[11px] font-extrabold px-2.5 py-0.5 rounded-full border-2 border-card bg-amber-400 text-slate-900 whitespace-nowrap">
                1st · {winner.you ? "You" : winner.name}
              </span>
            </div>
          )}
        </div>
        <div className="px-5 pt-12 pb-[18px] text-center">
          <h2 className="text-[21px] font-extrabold leading-tight" data-testid="race-result-title">
            {youWon ? "You won the race!" : `${winner?.name || "Your rival"} won the race`}
          </h2>
          <p className="mt-1 mb-3 text-[13px] text-muted-foreground">{reason}</p>
          {(rows || round || typeof eloDelta === "number") && (
            <div className="flex flex-wrap gap-1.5 justify-center mb-4 text-[11px]">
              {rows && <span className="bg-muted rounded-lg px-2 py-1 text-muted-foreground">Rows to go <b className="text-foreground">{rows}</b></span>}
              {round ? <span className="bg-muted rounded-lg px-2 py-1 text-muted-foreground">Round <b className="text-foreground">{Math.min(round, 15)}</b> of 15</span> : null}
              {/* Races against people have their own Elo (Jack, 2026-10-28). */}
              {typeof eloDelta === "number" && (
                <span className={`rounded-lg px-2 py-1 font-bold ${eloDelta > 0 ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                  : eloDelta < 0 ? "bg-red-500/10 text-red-600 dark:text-red-300" : "bg-muted text-muted-foreground"}`} data-testid="race-result-elo">
                  Race Elo {eloDelta > 0 ? `+${eloDelta}` : eloDelta}
                </span>
              )}
            </div>
          )}
          {rematchState === "incoming" && (
            <div className="mb-2" data-testid="rematch-incoming">
              <p className="text-[13px] text-muted-foreground mb-1.5"><b className="text-foreground">{oppName}</b> wants to race again</p>
              <div className="flex gap-2">
                <button type="button" onClick={onDecline} data-testid="rematch-decline-btn"
                  className="flex-1 h-11 rounded-xl border border-destructive/40 text-destructive text-[14px] font-bold hover:bg-destructive/10">Decline</button>
                <button type="button" onClick={onAccept} data-testid="rematch-accept-btn"
                  className="flex-1 h-11 rounded-xl bg-[#0288d1] hover:bg-[#039be5] text-white font-extrabold text-[15px]">Accept</button>
              </div>
            </div>
          )}
          {rematchState === "pending" && (
            <div className="w-full h-11 rounded-xl border border-border text-muted-foreground text-[13px] font-medium mb-2 flex items-center justify-center gap-2 px-3" data-testid="rematch-pending-btn">
              <CircleNotch size={16} weight="bold" className="animate-spin" />
              <span className="truncate">Waiting for {oppName}…</span>
              <button type="button" aria-label="Cancel rematch" onClick={onCancel} data-testid="rematch-cancel-btn"
                className="ml-auto w-6 h-6 rounded-full inline-flex items-center justify-center hover:text-destructive"><X size={14} weight="bold" /></button>
            </div>
          )}
          {rematchState === "declined" && (
            <div className="w-full h-11 rounded-xl border border-destructive/30 bg-destructive/5 text-destructive text-[13px] font-medium mb-2 flex items-center justify-center" data-testid="rematch-declined-banner">
              {oppName} declined the rematch
            </div>
          )}
          {onAgain && rematchState === "idle" && (
            <button type="button" onClick={onAgain} data-testid="race-again"
              className="w-full h-11 rounded-xl bg-[#0288d1] hover:bg-[#039be5] text-white font-extrabold text-[15px] mb-2 flex items-center justify-center gap-1.5">
              <ArrowClockwise size={16} weight="bold" />{againLabel}
            </button>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onView} className="flex-1 h-[38px] rounded-xl border border-border text-[13px] font-bold text-muted-foreground hover:text-foreground" data-testid="race-result-view">View board</button>
            <button type="button" onClick={onLobby} className="flex-1 h-[38px] rounded-xl border border-border text-[13px] font-bold text-muted-foreground hover:text-foreground" data-testid="race-result-lobby">Lobby</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// The header after a race: a coloured result pill that reopens the pop-up
// (just "Won" / "Lost" on phones, where the game header is tight).
export function RaceResultPill({ won, place, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid="race-result-chip"
      className={`text-xs font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${won
        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40"
        : "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/35"}`}>
      {won ? <>🏆 <span className="hidden sm:inline">1st · You won</span><span className="sm:hidden">Won</span></>
        : <>{place === "3rd" ? "🥉" : "🥈"} <span className="hidden sm:inline">{place || "2nd"} · You lost</span><span className="sm:hidden">Lost</span></>}
    </button>
  );
}
